import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAbout, updateAbout, uploadAboutImage } from '../../services/api';
import '../../styles/Admin.css';

const EditAbout = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    profile_image: null,
    years_experience: 0,
    projects_completed: 0
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchAboutData();
  }, [navigate]);

  const fetchAboutData = async () => {
    try {
      const response = await getAbout();
      const data = response.data || response;
      if (data) {
        setFormData({
          title: data.title || '',
          description: data.description || '',
          profile_image: data.profile_image || null,
          years_experience: data.years_experience || 0,
          projects_completed: data.projects_completed || 0
        });
        if (data.profile_image) {
          setImagePreview(`${process.env.REACT_APP_API_URL}${data.profile_image}`);
        }
      }
    } catch (error) {
      console.error('Error fetching about data:', error);
      setMessage({ type: 'error', text: 'فشل في تحميل البيانات' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'years_experience' || name === 'projects_completed' 
        ? parseInt(value) || 0 
        : value
    });
    
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'يرجى اختيار ملف صورة' });
        return;
      }

      if (file.size > 25 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'يجب أن يكون حجم الصورة أقل من 25 ميجابايت' });
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!imageFile) {
      setMessage({ type: 'error', text: 'يرجى اختيار صورة أولاً' });
      return;
    }

    setUploadingImage(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await uploadAboutImage(imageFile);
      setFormData({
        ...formData,
        profile_image: response.imageUrl
      });
      setMessage({ type: 'success', text: 'تم رفع الصورة بنجاح!' });
      setImageFile(null);
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'فشل في رفع الصورة' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description) {
      setMessage({ type: 'error', text: 'يرجى ملء جميع الحقول المطلوبة' });
      return;
    }

    if (formData.years_experience < 0 || formData.projects_completed < 0) {
      setMessage({ type: 'error', text: 'الأرقام لا يمكن أن تكون سالبة' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await updateAbout(formData);
      setMessage({ type: 'success', text: 'تم تحديث قسم النبذة بنجاح!' });
    } catch (error) {
      console.error('Error updating about:', error);
      setMessage({ type: 'error', text: 'فشل في تحديث قسم النبذة' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout" dir="rtl">
      {/* Mobile Menu Button */}
      <button 
        className={`mobile-menu-btn ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2 className="admin-sidebar-logo">لوحة التحكم</h2>
          <p className="admin-sidebar-subtitle">إدارة المعرض</p>
        </div>

        <nav>
          <ul className="admin-nav">
            <li className="admin-nav-item">
              <Link to="/admin/dashboard" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📊</span>
                <span>الرئيسية</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/hero" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🎯</span>
                <span>قسم الرئيسي</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/about" className="admin-nav-link active" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">👤</span>
                <span>نبذة عني</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/projects" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🏗️</span>
                <span>المشاريع</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/contact" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📧</span>
                <span>معلومات التواصل</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/change-password" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🔒</span>
                <span>تغيير كلمة المرور</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <a href="/" className="admin-nav-link" target="_blank" rel="noopener noreferrer">
                <span className="nav-icon">🌐</span>
                <span>عرض الموقع</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <h1>تعديل قسم النبذة</h1>
          <p>تحديث معلوماتك الشخصية وإنجازاتك</p>
        </div>

        <div className="admin-content">
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-form">
            {/* Profile Image */}
            <div className="form-group">
              <label>
                الصورة الشخصية
                <span className="field-hint">رفع صورتك المهنية (الحد الأقصى 25 ميجابايت)</span>
              </label>

              {imagePreview && (
                <div className="image-preview profile-preview">
                  <img src={imagePreview} alt="الصورة الشخصية" />
                </div>
              )}

              <div className="file-upload-wrapper">
                <input
                  type="file"
                  id="profile-image"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={uploadingImage}
                />
                <label htmlFor="profile-image" className="file-upload-label">
                  <span className="upload-icon">📁</span>
                  {imageFile ? imageFile.name : 'اختر صورة'}
                </label>
              </div>

              {imageFile && (
                <button
                  type="button"
                  onClick={handleUploadImage}
                  className="btn-upload"
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <span className="spinner-small"></span>
                      جاري الرفع...
                    </>
                  ) : (
                    'رفع الصورة'
                  )}
                </button>
              )}
            </div>

            {/* Title */}
            <div className="form-group">
              <label htmlFor="title">
                عنوان القسم *
                <span className="field-hint">عنوان قسم النبذة</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="مثال: نبذة عني"
                disabled={saving}
                required
              />
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">
                الوصف *
                <span className="field-hint">أخبر الزوار عن نفسك وعملك</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="اكتب عن خبرتك وشغفك وما يدفعك كمهندس معماري..."
                rows="8"
                disabled={saving}
                required
              />
            </div>

            {/* Statistics */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="years_experience">
                  سنوات الخبرة
                  <span className="field-hint">كم سنة في المجال</span>
                </label>
                <input
                  type="number"
                  id="years_experience"
                  name="years_experience"
                  value={formData.years_experience}
                  onChange={handleChange}
                  min="0"
                  placeholder="مثال: 5"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="projects_completed">
                  المشاريع المنجزة
                  <span className="field-hint">إجمالي عدد المشاريع المكتملة</span>
                </label>
                <input
                  type="number"
                  id="projects_completed"
                  name="projects_completed"
                  value={formData.projects_completed}
                  onChange={handleChange}
                  min="0"
                  placeholder="مثال: 20"
                  disabled={saving}
                />
              </div>
            </div>

            {/* Preview Section */}
            <div className="preview-section">
              <h3>معاينة</h3>
              <div className="about-preview">
                <div className="about-preview-image">
                  {imagePreview ? (
                    <img src={imagePreview} alt="معاينة الصورة الشخصية" />
                  ) : (
                    <div className="preview-placeholder">👤</div>
                  )}
                </div>
                <div className="about-preview-content">
                  <h2>{formData.title || 'عنوان القسم'}</h2>
                  <p>{formData.description || 'سيظهر وصفك هنا...'}</p>
                  <div className="preview-stats">
                    <div className="preview-stat">
                      <strong>{formData.years_experience}+</strong>
                      <span>سنوات الخبرة</span>
                    </div>
                    <div className="preview-stat">
                      <strong>{formData.projects_completed}+</strong>
                      <span>مشروع منجز</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-save"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-small"></span>
                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    حفظ التغييرات
                  </>
                )}
              </button>

              <Link to="/admin/dashboard" className="btn-cancel">
                إلغاء
              </Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditAbout;